/**
 * Roles API — TanStack Query factory.
 *
 * Phase-2 Wave-3 W3.T2. Same 5-minute `staleTime` as departments —
 * roles are seed-driven and never edited at runtime in Phase 2.
 *
 * The BE silently downgrades `?includeUnassignable=true` for actors
 * below ADMIN tier (apps/api/src/roles/roles.controller.ts L36-43), so
 * the FE can pass the flag whenever the call-site might benefit and
 * trust the server to clamp it.
 */
import { useQuery } from '@tanstack/react-query';

import type { AccessLevel, RoleSlug } from '@aris/shared';

import { apiFetch } from '@/lib/fetcher';
import { qk } from '@/lib/query-keys';

export interface RoleRow {
  id: number;
  name: string;
  slug: RoleSlug;
  accessLevel: AccessLevel;
  isAssignable: boolean;
}

export interface UseRolesQueryOptions {
  /**
   * Pass through to the `?includeUnassignable=` query param. Defaults to
   * `false`; the BE will downgrade when the actor isn't entitled.
   */
  includeUnassignable?: boolean;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export function useRolesQuery(options: UseRolesQueryOptions = {}) {
  const { includeUnassignable = false } = options;
  return useQuery<RoleRow[]>({
    // The query key includes the flag so toggling it doesn't return the
    // wrong cached payload.
    queryKey: [...qk.roles.list(), { includeUnassignable }] as const,
    queryFn: () =>
      apiFetch<RoleRow[]>(includeUnassignable ? '/roles?includeUnassignable=true' : '/roles'),
    staleTime: FIVE_MINUTES_MS,
  });
}
